// MyDesk — list of classes the student has been assigned to.
// Shows each class with its most recent published lesson and a "Open class" CTA.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, ChevronRight, Library } from 'lucide-react';
import api from '../studentApi';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

export default function MyDeskPage() {
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [totalLessons, setTotal] = useState(0);

  useEffect(() => {
    let mounted = true;
    api.get('/student/mydesk/overview')
      .then(d => { if (mounted) { setClasses(d.classes || []); setTotal(d.total_lessons || 0); } })
      .catch(() => { if (mounted) setClasses([]); })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <div className="space-y-8" data-testid="mydesk-page">
      <header>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border text-[11px] font-semibold uppercase tracking-widest"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', color: 'var(--st-mute)' }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--st-primary)' }} />
          MyDesk
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mt-3" style={{ fontFamily: 'var(--st-font-heading)' }}>
          Your classes, chapter by chapter.
        </h1>
        <p className="text-sm mt-2" style={{ color: 'var(--st-mute)' }}>
          Every lesson your teachers publish lives here. {totalLessons > 0 && `${totalLessons} lesson${totalLessons === 1 ? '' : 's'} across ${classes.length} class${classes.length === 1 ? '' : 'es'}.`}
        </p>
      </header>

      {loading ? (
        <div className="py-20 text-center text-sm" style={{ color: 'var(--st-mute)' }}>Loading your desk…</div>
      ) : classes.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {classes.map(c => <ClassCard key={c.class_id} c={c} />)}
        </div>
      )}
    </div>
  );
}

function ClassCard({ c }) {
  return (
    <Link to={`/student/mydesk/class/${c.class_id}`}
      data-testid={`mydesk-class-${c.class_id}`}
      className="group relative rounded-2xl p-5 border-2 transition-all hover:-translate-y-0.5"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', color: 'var(--st-ink)' }}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
          <Library className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">
            {c.grade || 'Class'}{c.subject_hint ? ` · ${c.subject_hint}` : ''}
          </div>
          <h3 className="text-base font-bold leading-tight mt-0.5 truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{c.class_name}</h3>
          <div className="flex items-center gap-3 mt-2 text-[11px]" style={{ color: 'var(--st-mute)' }}>
            <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> {c.lesson_count} lessons</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 opacity-30 group-hover:opacity-80 transition-all shrink-0 mt-1" />
      </div>

      {c.next_up ? (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--st-border)' }}>
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--st-primary)' }}>Latest</div>
          <div className="text-sm font-semibold leading-tight mt-0.5 line-clamp-2">{c.next_up.title}</div>
          {c.next_up.summary && <p className="text-[11px] mt-1 opacity-70 line-clamp-2">{c.next_up.summary}</p>}
          <div className="text-[10px] mt-2 inline-flex items-center gap-1 opacity-60">
            <Calendar className="w-3 h-3" /> {fmtDate(c.next_up.published_at)}
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t text-[11px]" style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>
          No lessons published yet.
        </div>
      )}
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border-2 border-dashed p-10 text-center" style={{ borderColor: 'var(--st-border)' }} data-testid="mydesk-empty">
      <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-4"
        style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)' }}>
        <Library className="w-6 h-6" style={{ color: 'var(--st-primary)' }} />
      </div>
      <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--st-font-heading)' }}>No classes assigned yet</h3>
      <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: 'var(--st-mute)' }}>
        Once your institution assigns you to a class, every lesson your teacher publishes appears here — neatly grouped by chapter and date.
      </p>
    </div>
  );
}
